/**
 * Held-item display transforms (vanilla item model `display` blocks) shared by the third-person
 * player body and every mob that holds something.
 *
 * `heldKindOfItem` classifies an item the way vanilla's parent models do (item/generated,
 * item/handheld, bow, crossbow ...) and `applyThirdPerson` poses a mesh in the humanoid's
 * ItemInHandLayer frame (Humanoid.rightHand / leftHand, block units) with the matching
 * `thirdperson_righthand` display values. Meshes are 1x1 centred sprites facing +Z (extras.itemSpriteMesh,
 * itemEntities.spriteGeometry) or centred 1-block cubes.
 */

import * as THREE from 'three';
import { itemNameOf, ITEM_BASE } from '../game/itemIds';

export type HeldKind = 'flat' | 'handheld' | 'rod' | 'block' | 'bow' | 'crossbow' | 'trident' | 'spear';

/** One ItemTransform: rotation degrees, translation in 1/16 block, uniform scale. */
interface Display { rot: readonly [number, number, number]; trans: readonly [number, number, number]; scale: number }

/** Vanilla `thirdperson_righthand` per parent model. */
export const TP_DISPLAY: Record<HeldKind, Display> = {
  flat: { rot: [0, 0, 0], trans: [0, 3, 1], scale: 0.55 },
  handheld: { rot: [0, -90, 55], trans: [0, 4, 0.5], scale: 0.85 },
  rod: { rot: [0, 90, 55], trans: [0, 4, 2.5], scale: 0.85 },
  block: { rot: [75, 45, 0], trans: [0, 2.5, 0], scale: 0.375 },
  bow: { rot: [-80, 260, -40], trans: [-1, -2, 2.5], scale: 0.9 },
  crossbow: { rot: [-90, 0, -60], trans: [2, 0.1, -3], scale: 0.9 },
  // the 3D trident model (render/tridentModel.ts), grip at the hand, prongs along the hand's forward axis
  trident: { rot: [35, 0, 0], trans: [0, 0, 0], scale: 1 },
  // spears: the 32x32 `<tier>_spear_in_hand` sprite (2x the icon's length) held low, tip forward-up
  spear: { rot: [0, -90, 78], trans: [0, 1, 0.5], scale: 1.7 },
};

/** Vanilla `firstperson_righthand` for the kinds the first-person hand shares (flat/block). */
export const FP_DISPLAY: Partial<Record<HeldKind, Display>> = {
  flat: { rot: [0, -90, 25], trans: [1.13, 3.2, 1.13], scale: 0.68 },
  handheld: { rot: [0, -90, 25], trans: [1.13, 3.2, 1.13], scale: 0.68 },
  block: { rot: [0, 45, 0], trans: [0, 0, 0], scale: 0.4 },
};

const HANDHELD = /_(SWORD|PICKAXE|AXE|SHOVEL|HOE)$|^(STICK|BLAZE_ROD|BREEZE_ROD|MACE)$/;

/** Kind of an item id (ids below ITEM_BASE are blocks: callers decide cube vs flat tile themselves). */
export function heldKindOfItem(id: number): HeldKind {
  if (id < ITEM_BASE) return 'flat';
  const n: string = itemNameOf(id) ?? '';
  if (n === 'BOW') return 'bow';
  if (n === 'CROSSBOW') return 'crossbow';
  if (n === 'TRIDENT') return 'trident';
  if (n.endsWith('_SPEAR')) return 'spear';
  if (n === 'CARROT_ON_A_STICK' || n === 'WARPED_FUNGUS_ON_A_STICK' || n === 'FISHING_ROD') return 'rod';
  return HANDHELD.test(n) ? 'handheld' : 'flat';
}

const DEG = Math.PI / 180;

/**
 * Pose `o` in a hand with the vanilla display values (Rz * Ry * Rx, the order ItemTransform uses).
 * The left hand is the mirror image: translation x and the Y / Z rotations negate.
 */
export function applyThirdPerson(o: THREE.Object3D, kind: HeldKind, left = false): void {
  const d = TP_DISPLAY[kind];
  const f = left ? -1 : 1;
  o.rotation.order = 'ZYX';
  o.rotation.set(d.rot[0] * DEG, d.rot[1] * f * DEG, d.rot[2] * f * DEG);
  o.position.set(d.trans[0] * f / 16, d.trans[1] / 16, d.trans[2] / 16);
  o.scale.setScalar(d.scale);
}
